import { useEffect, useRef, type RefObject } from 'react';

/**
 * Call `onOutside` when a pointer press lands outside `ref`'s element.
 *
 * For an overlay that is NOT a popover: a results panel under a search box,
 * a dropdown anchored to a field the user keeps typing into. `Popover`
 * owns the toggle-trigger case (it clones a trigger and composes the
 * toggle onto it); this hook owns the "dismiss when the user presses
 * elsewhere" half for everything that has no trigger to clone.
 *
 * The listener is attached only while `enabled`, on `mousedown` so it fires
 * before the press can move focus, and it reads the latest `onOutside`
 * through a ref — a new callback per render does not resubscribe.
 */
export function useOutsidePress(
  ref: RefObject<HTMLElement>,
  onOutside: () => void,
  enabled = true,
): void {
  const handlerRef = useRef(onOutside);
  useEffect(() => {
    handlerRef.current = onOutside;
  }, [onOutside]);

  useEffect(() => {
    if (!enabled) return undefined;
    const handler = (event: MouseEvent): void => {
      const element = ref.current;
      if (element !== null && !element.contains(event.target as Node)) {
        handlerRef.current();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ref, enabled]);
}
